/**
 * KRA Photo Journal – TagFilterPanel
 *
 * Slide-in drawer panel for filtering photos by tags.
 * Shows 4 grouped sections (Trang phục / Kiểu chụp / Mùa / Cảnh).
 *
 * Filter logic:
 *   - AND between groups
 *   - OR within group
 *
 * Options shown = all tags present in currently loaded photos.
 */

import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { PhotoRecord } from "@kra/photo-journal-shared";
import { TAG_GROUPS, type TagFilters, collectTagOptions, countActiveTagFilters } from "../lib/tagHelpers.js";
import { TagChip } from "./TagChip.js";
import type { TagOptions } from "../lib/api.js";
import { cn } from "../lib/utils.js";

interface TagFilterPanelProps {
  isOpen: boolean;
  onClose: () => void;
  /** All loaded photos — used as fallback when Airtable schema options are unavailable */
  photos: PhotoRecord[];
  /** Full option list from the Airtable multi-select schema. When present,
   *  every defined option is shown — even if no loaded photo uses it yet. */
  tagOptions?: TagOptions;
  filters: TagFilters;
  onFiltersChange: (filters: TagFilters) => void;
}

export function TagFilterPanel({ isOpen, onClose, photos, tagOptions, filters, onFiltersChange }: TagFilterPanelProps) {
  function toggleTag(groupKey: (typeof TAG_GROUPS)[number]["key"], value: string) {
    const current = filters[groupKey] ?? [];
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    onFiltersChange({ ...filters, [groupKey]: next });
  }

  function clearAll() {
    onFiltersChange({});
  }

  const activeCount = countActiveTagFilters(filters);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            key="tag-filter-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-30 bg-black/40"
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            key="tag-filter-panel"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.28, ease: [0.25, 0.46, 0.45, 0.94] }}
            className={cn(
              "fixed right-0 top-0 bottom-0 z-40",
              "w-72 bg-surface-0 border-l border-surface-2",
              "flex flex-col overflow-hidden",
              "shadow-2xl"
            )}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-surface-2 flex-shrink-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-ink-0">Lọc theo tag</span>
                {activeCount > 0 && (
                  <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-ink-0 text-surface-0">
                    {activeCount}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {activeCount > 0 && (
                  <button
                    onClick={clearAll}
                    className="text-xs text-ink-2 hover:text-ink-0 transition-colors"
                  >
                    Xóa tất cả
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="w-7 h-7 flex items-center justify-center rounded-full bg-surface-2 text-ink-2 hover:text-ink-0 transition-colors"
                  aria-label="Đóng"
                >
                  <svg width={11} height={11} viewBox="0 0 11 11" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
                    <path d="M1 1l9 9M10 1L1 10" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Groups */}
            <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-5">
              {TAG_GROUPS.map((group) => {
                const schemaOptions = tagOptions?.[group.fieldName];
                const options = schemaOptions && schemaOptions.length > 0
                  ? [...schemaOptions].sort((a, b) => a.localeCompare(b, "vi"))
                  : collectTagOptions(photos, group.key);
                const selected = filters[group.key] ?? [];

                if (options.length === 0) return null;

                return (
                  <div key={group.key}>
                    <div className="flex items-center gap-1.5 mb-2">
                      <span className="text-sm">{group.emoji}</span>
                      <span className="text-xs font-semibold text-ink-1">{group.label}</span>
                      {selected.length > 0 && (
                        <span className="ml-auto text-[10px] text-ink-3 cursor-pointer hover:text-ink-1"
                          onClick={() => onFiltersChange({ ...filters, [group.key]: [] })}>
                          Xóa
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {options.map((opt) => {
                        const isActive = selected.includes(opt);
                        return (
                          <button
                            key={opt}
                            onClick={() => toggleTag(group.key, opt)}
                            className={cn(
                              "px-2.5 py-1 rounded-full text-xs font-medium transition-colors duration-150",
                              isActive
                                ? "bg-ink-0 text-surface-0"
                                : "bg-surface-1 text-ink-1 hover:bg-surface-3"
                            )}
                          >
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {TAG_GROUPS.every((g) => {
                const schema = tagOptions?.[g.fieldName];
                if (schema && schema.length > 0) return false;
                return collectTagOptions(photos, g.key).length === 0;
              }) && (
                <div className="text-center py-8 text-xs text-ink-3">
                  Chưa có tag nào được đặt
                </div>
              )}
            </div>

            {/* Footer hint */}
            <div className="px-4 py-2.5 border-t border-surface-2 flex-shrink-0">
              <p className="text-[10px] text-ink-3 text-center leading-relaxed">
                AND giữa nhóm · OR trong nhóm
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
